export interface WxbModalSize {
  width: number;
  height: number;
}

export interface WxbModalResizeLimits {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  viewportMargin?: number;
}

export interface WxbModalResizeViewport {
  width?: number;
  height?: number;
}

export interface WxbModalResizeBounds {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
}

export const WXB_MODAL_DEFAULT_RESIZE_SIZE: WxbModalSize = {
  width: 720,
  height: 520,
};

export const WXB_MODAL_DEFAULT_RESIZE_LIMITS = {
  minWidth: 360,
  minHeight: 260,
  maxWidth: 1280,
  maxHeight: 900,
  viewportMargin: 32,
};

const FALLBACK_VIEWPORT_MIN_WIDTH = 280;
const FALLBACK_VIEWPORT_MIN_HEIGHT = 220;

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function toPositiveNumber(value: unknown, fallback: number): number {
  return isPositiveFiniteNumber(value) ? value : fallback;
}

function readViewport(viewport?: WxbModalResizeViewport): Required<WxbModalResizeViewport> | null {
  const viewportWidth = viewport?.width;
  const viewportHeight = viewport?.height;

  if (isPositiveFiniteNumber(viewportWidth) && isPositiveFiniteNumber(viewportHeight)) {
    return {
      width: viewportWidth,
      height: viewportHeight,
    };
  }

  if (typeof window === 'undefined') return null;

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

export function getWxbModalResizeBounds(
  limits: WxbModalResizeLimits = {},
  viewport?: WxbModalResizeViewport,
): WxbModalResizeBounds {
  const viewportSize = readViewport(viewport);
  const viewportMargin = Math.max(0, toPositiveNumber(
    limits.viewportMargin,
    WXB_MODAL_DEFAULT_RESIZE_LIMITS.viewportMargin,
  ));

  const limitMinWidth = toPositiveNumber(limits.minWidth, WXB_MODAL_DEFAULT_RESIZE_LIMITS.minWidth);
  const limitMinHeight = toPositiveNumber(limits.minHeight, WXB_MODAL_DEFAULT_RESIZE_LIMITS.minHeight);
  const limitMaxWidth = Math.max(
    limitMinWidth,
    toPositiveNumber(limits.maxWidth, WXB_MODAL_DEFAULT_RESIZE_LIMITS.maxWidth),
  );
  const limitMaxHeight = Math.max(
    limitMinHeight,
    toPositiveNumber(limits.maxHeight, WXB_MODAL_DEFAULT_RESIZE_LIMITS.maxHeight),
  );

  const viewportMaxWidth = viewportSize
    ? Math.max(FALLBACK_VIEWPORT_MIN_WIDTH, viewportSize.width - viewportMargin * 2)
    : limitMaxWidth;
  const viewportMaxHeight = viewportSize
    ? Math.max(FALLBACK_VIEWPORT_MIN_HEIGHT, viewportSize.height - viewportMargin * 2)
    : limitMaxHeight;

  const maxWidth = Math.min(limitMaxWidth, viewportMaxWidth);
  const maxHeight = Math.min(limitMaxHeight, viewportMaxHeight);

  return {
    minWidth: Math.min(limitMinWidth, maxWidth),
    minHeight: Math.min(limitMinHeight, maxHeight),
    maxWidth,
    maxHeight,
  };
}

export function clampWxbModalSize(
  size: WxbModalSize,
  bounds: WxbModalResizeBounds,
): WxbModalSize {
  return {
    width: Math.min(bounds.maxWidth, Math.max(bounds.minWidth, size.width)),
    height: Math.min(bounds.maxHeight, Math.max(bounds.minHeight, size.height)),
  };
}

export function readNumericModalDimension(value: unknown): number | undefined {
  if (isPositiveFiniteNumber(value)) return value;

  if (typeof value !== 'string') return undefined;

  const trimmedValue = value.trim();
  if (!trimmedValue.endsWith('px')) return undefined;

  const parsedValue = Number.parseFloat(trimmedValue);
  return isPositiveFiniteNumber(parsedValue) ? parsedValue : undefined;
}

export function getWxbModalInitialResizeSize(
  width: unknown,
  defaultSize: Partial<WxbModalSize> | undefined,
  limits?: WxbModalResizeLimits,
  viewport?: WxbModalResizeViewport,
): WxbModalSize {
  const bounds = getWxbModalResizeBounds(limits, viewport);
  return clampWxbModalSize({
    width: defaultSize?.width
      ?? readNumericModalDimension(width)
      ?? WXB_MODAL_DEFAULT_RESIZE_SIZE.width,
    height: defaultSize?.height ?? WXB_MODAL_DEFAULT_RESIZE_SIZE.height,
  }, bounds);
}
